/* Ingredient library: client-side search, filters, A to Z, paging, and URL state.
   Markup, data attributes and card order (sorted in Liquid) come from sections/ingredient-library.liquid.
   Cards are hidden with the `hidden` attribute (never .is-hidden: app.css owns that). */
(function () {
  'use strict';
  if (customElements.get('ingredient-library')) return;

  var PARAMS = { q: 'q', family: 'family', benefit: 'benefit', part: 'part', letter: 'letter', page: 'page' };
  var FILTER_KEYS = ['q', 'family', 'benefit', 'part', 'letter'];

  class IngredientLibrary extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-il-grid]');
      if (!this.grid) return;
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-il-card]'));
      this.search = this.querySelector('[data-il-search]');
      this.count = this.querySelector('[data-il-count]');
      this.totalEl = this.querySelector('[data-il-total]');
      this.pager = this.querySelector('[data-il-pager]');
      this.alpha = this.querySelector('[data-il-alpha]');
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 0;
      this.empty = this.querySelector('[data-il-empty]');
      this.more = this.querySelector('[data-il-more]');
      this.activeBadge = this.querySelector('[data-il-active-count]');
      this.clears = Array.prototype.slice.call(this.querySelectorAll('[data-il-clear]'));
      this.state = { q: '', family: '', benefit: '', part: '', letter: '', page: 1 };

      this.readUrl();
      this.bind();
      this.syncControls();
      this.apply(false);
      this.watchViewport();
    }

    readUrl() {
      var p = new URLSearchParams(window.location.search);
      this.state.q = (p.get(PARAMS.q) || '').trim();
      this.state.family = (p.get(PARAMS.family) || '').toLowerCase();
      this.state.benefit = (p.get(PARAMS.benefit) || '').toLowerCase();
      this.state.part = (p.get(PARAMS.part) || '').toLowerCase();
      this.state.letter = (p.get(PARAMS.letter) || '').toUpperCase().slice(0, 1);
      this.state.page = Math.max(1, parseInt(p.get(PARAMS.page), 10) || 1);
    }

    writeUrl() {
      var p = new URLSearchParams(window.location.search);
      Object.keys(PARAMS).forEach(function (k) { p.delete(PARAMS[k]); });
      if (this.state.q) p.set(PARAMS.q, this.state.q);
      if (this.state.family) p.set(PARAMS.family, this.state.family);
      if (this.state.benefit) p.set(PARAMS.benefit, this.state.benefit);
      if (this.state.part) p.set(PARAMS.part, this.state.part);
      if (this.state.letter) p.set(PARAMS.letter, this.state.letter);
      if (this.perPage && this.state.page > 1) p.set(PARAMS.page, String(this.state.page));
      var qs = p.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      try { window.history.replaceState(null, '', url); } catch (e) { /* ignore */ }
    }

    bind() {
      var self = this;
      if (this.search) {
        var timer;
        this.search.addEventListener('input', function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            self.state.q = self.search.value.trim();
            if (self.state.q) self.state.letter = '';
            self.state.page = 1;
            self.apply(true);
          }, 120);
        });
      }
      this.querySelectorAll('[data-il-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var group = btn.getAttribute('data-il-filter');
          var value = (btn.getAttribute('data-value') || '').toLowerCase();
          self.state[group] = self.state[group] === value ? '' : value;
          self.state.page = 1;
          self.apply(true);
        });
      });
      this.querySelectorAll('[data-il-letter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var l = btn.getAttribute('data-il-letter');
          self.state.letter = self.state.letter === l ? '' : l;
          if (self.state.letter && self.search) { self.state.q = ''; self.search.value = ''; }
          self.state.page = 1;
          self.apply(true);
        });
      });
      if (this.pager) {
        this.pager.addEventListener('click', function (event) {
          var btn = event.target.closest('[data-il-page]');
          if (!btn || btn.disabled) return;
          var next = parseInt(btn.getAttribute('data-il-page'), 10);
          if (!next || next === self.state.page) return;
          self.state.page = next;
          self.apply(true);
          self.scrollToGrid();
        });
      }
      this.clears.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.state = { q: '', family: '', benefit: '', part: '', letter: '', page: 1 };
          if (self.search) self.search.value = '';
          self.apply(true);
          if (self.search && window.matchMedia('(min-width: 768px)').matches) self.search.focus();
        });
      });
    }

    syncControls() {
      var self = this;
      if (this.search && this.search.value !== this.state.q) this.search.value = this.state.q;
      this.querySelectorAll('[data-il-filter]').forEach(function (btn) {
        var group = btn.getAttribute('data-il-filter');
        var value = (btn.getAttribute('data-value') || '').toLowerCase();
        var on = self.state[group] === value;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('il__pill--on', on);
      });
      this.querySelectorAll('[data-il-letter]').forEach(function (btn) {
        var on = btn.getAttribute('data-il-letter') === self.state.letter;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        // On the phone strip (horizontal scroll) keep the active letter in view.
        if (on && self.alpha && self.alpha.scrollWidth > self.alpha.clientWidth + 1) {
          var left = btn.offsetLeft - (self.alpha.clientWidth - btn.offsetWidth) / 2;
          self.alpha.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      });
      var active = [this.state.benefit, this.state.part].filter(Boolean).length;
      if (this.activeBadge) {
        this.activeBadge.textContent = active ? String(active) : '';
        this.activeBadge.hidden = !active;
      }
      var any = FILTER_KEYS.some(function (k) { return !!self.state[k]; });
      this.clears.forEach(function (btn) {
        if (btn.closest('[data-il-empty]')) return;
        btn.hidden = !any;
      });
    }

    matches(card) {
      var s = this.state;
      if (s.q) {
        var name = card.getAttribute('data-name') || '';
        var q = s.q.toLowerCase();
        if (name.indexOf(q) === -1 && (card.getAttribute('data-benefits') || '').indexOf(q) === -1 && (card.getAttribute('data-part') || '').indexOf(q) === -1) return false;
      }
      if (s.family && (card.getAttribute('data-family') || '') !== s.family) return false;
      if (s.part && (card.getAttribute('data-part') || '') !== s.part) return false;
      if (s.benefit && ((card.getAttribute('data-benefits') || '') + '|||').indexOf(s.benefit + '|||') === -1) return false;
      if (s.letter && (card.getAttribute('data-letter') || '') !== s.letter) return false;
      return true;
    }

    apply(fromUser) {
      var self = this;
      var matched = this.cards.filter(function (card) { return self.matches(card); });
      var total = matched.length;
      var per = this.perPage;
      var pages = per ? Math.max(1, Math.ceil(total / per)) : 1;
      if (this.state.page > pages) this.state.page = pages;
      if (this.state.page < 1) this.state.page = 1;
      var start = per ? (this.state.page - 1) * per : 0;
      var end = per ? Math.min(total, start + per) : total;

      var visible = new Set(matched.slice(start, end));
      this.cards.forEach(function (card) { card.hidden = !visible.has(card); });

      if (this.count) this.count.textContent = pages > 1 ? (start + 1) + '\u2013' + end : String(total);
      if (this.totalEl) this.totalEl.textContent = String(total);
      if (this.empty) this.empty.hidden = total !== 0;
      this.grid.hidden = total === 0;
      this.renderPager(pages);
      this.syncControls();
      if (fromUser) this.writeUrl();
    }

    // Prev / 1 … 4 [5] 6 … 12 / Next. Rebuilt on every apply; hidden with one page.
    renderPager(pages) {
      if (!this.pager) return;
      if (pages <= 1) { this.pager.hidden = true; this.pager.innerHTML = ''; return; }
      var cur = this.state.page;
      var items = [];
      var push = function (n) { if (items.indexOf(n) === -1) items.push(n); };
      push(1);
      for (var n = cur - 1; n <= cur + 1; n++) if (n > 1 && n < pages) push(n);
      push(pages);
      items.sort(function (a, b) { return a - b; });

      var html = '<button type="button" class="il__page il__page--arrow" data-il-page="' + (cur - 1) + '"' + (cur === 1 ? ' disabled' : '') + ' aria-label="Previous page">&larr;</button>';
      var prev = 0;
      items.forEach(function (n) {
        if (n - prev > 1) html += '<span class="il__page-gap" aria-hidden="true">&hellip;</span>';
        html += '<button type="button" class="il__page" data-il-page="' + n + '"' + (n === cur ? ' aria-current="page"' : '') + ' aria-label="Page ' + n + '">' + n + '</button>';
        prev = n;
      });
      html += '<button type="button" class="il__page il__page--arrow" data-il-page="' + (cur + 1) + '"' + (cur === pages ? ' disabled' : '') + ' aria-label="Next page">&rarr;</button>';
      this.pager.innerHTML = html;
      this.pager.hidden = false;
    }

    // Bring the filter bar back under the header after a page change.
    scrollToGrid() {
      var offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      var target = this.querySelector('[data-il-bar]') || this.grid;
      var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 12;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    }

    watchViewport() {
      if (!this.more) return;
      var self = this;
      var mq = window.matchMedia('(min-width: 768px)');
      var update = function () {
        if (mq.matches) self.more.open = true;
        else if (!self.state.benefit && !self.state.part) self.more.open = false;
      };
      update();
      if (mq.addEventListener) mq.addEventListener('change', update);
      else if (mq.addListener) mq.addListener(update);
    }
  }

  customElements.define('ingredient-library', IngredientLibrary);
})();
